// Signed-URL resolution for sensitive uploads (audit S1, 2026-10-16).
// Public classes (avatars/condos) pass through untouched. Sensitive classes
// (/api/uploads/{payments,expenses,complaints,vehicles,notes}/...) require a
// short-TTL signed URL issued by GET /api/uploads/sign (backend verifies the
// requester's right to that specific file at issue time). Resolved signed URLs
// are cached until shortly before expiry so lists don't re-sign every render.
import api from './api';

const SENSITIVE_CLASSES = new Set(['payments', 'expenses', 'complaints', 'vehicles', 'notes']);

const cache = new Map<string, { url: string; expAt: number }>();

export function isSensitiveUploadUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  let path: string;
  try {
    path = url.startsWith('/') ? url : new URL(url, window.location.origin).pathname;
  } catch {
    return false;
  }
  if (!path.startsWith('/api/uploads/')) return false;
  const cls = path.split('/')[3] || '';
  return SENSITIVE_CLASSES.has(cls);
}

export async function resolveUploadUrl(url: string): Promise<string> {
  if (!isSensitiveUploadUrl(url)) return url;
  const hit = cache.get(url);
  const now = Date.now();
  if (hit && hit.expAt - now > 30_000) return hit.url;
  const res = await api.get('/uploads/sign', { params: { url } });
  const signedUrl: string | undefined = res.data?.data?.signedUrl;
  const expiresIn: number = Number(res.data?.data?.expiresIn) || 0;
  if (!signedUrl) throw new Error('No signed URL returned');
  cache.set(url, { url: signedUrl, expAt: now + expiresIn * 1000 });
  return signedUrl;
}
